import { ReactElement, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { EEinvoicingStatus, TInvoiceEinvoiceLinkEnriched } from '@Arti-zen/package-backoffice'
import { ApiArtizenBO } from 'utils/artizenConnector'
import TableInfinite from 'components/table/infinite/InfiniteTable'
import { definitionFields } from '../EinvoicingTable.definitionFields'
import DownloadButton from 'components/DownloadButton'
import { EinvoivingError } from '../Einvoiving.error'
import { DateTime } from 'luxon'
import { Dialog } from '@mui/material'
import { DaySelector } from './EinvoicingDayCtrl.DaySelector'
import { StatusListSelect } from './EinvoicingDayCtrl.StatusListSelect'

export default function EinvoicingDayCtrl(): ReactElement {
  const [eInvoiceList, setEInvoiceList] = useState<TInvoiceEinvoiceLinkEnriched[]>([])
  const [filters, setFilters] = useState<EEinvoicingStatus[] | null>(null)
  const [itemSelected, setItemSelected] = useState<TInvoiceEinvoiceLinkEnriched | null>(null)
  const [daySelected, setDaySelected] = useState<DateTime>(DateTime.now())
  const [isLoading, setIsLoading] = useState(false)

  const { nb, nbOk, nbTested } = useMemo(() => {
    return {
      nb: eInvoiceList.length,
      nbTested: eInvoiceList.filter((f) => f.state !== EEinvoicingStatus.TEST_COMPANY_NOT_READY).length,
      nbOk: eInvoiceList.filter((f) => f.state === EEinvoicingStatus.TEST_SUCCESS).length,
    }
  }, [eInvoiceList])

  const statusList = useMemo(() => {
    return eInvoiceList.reduce((acc, f) => {
      acc[f.state] = (acc[f.state] || 0) + 1
      return acc
    }, {} as Partial<Record<EEinvoicingStatus, number>>)
  }, [eInvoiceList])

  useEffect(() => {
    setFilters(Object.keys(statusList) as EEinvoicingStatus[])
  }, [statusList])

  const displayListFiltered = useMemo(() => {
    if (!filters) return eInvoiceList

    return eInvoiceList.filter((f) => filters.includes(f.state))
  }, [eInvoiceList, filters])

  useEffect(() => {
    setEInvoiceList([])
    setFilters(null)
    setIsLoading(true)
    ApiArtizenBO.eInvoicing
      .getAllByDay(daySelected.toJSDate())
      .then(setEInvoiceList)
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }, [daySelected])

  const onSelectDay = (day: DateTime) => {
    setDaySelected(day)
  }

  const onSelect = (item: TInvoiceEinvoiceLinkEnriched) => {
    setItemSelected(item)
  }

  return (
    <Wrapper>
      <Header>
        <DaySelector daySelected={daySelected} onSelectDay={onSelectDay} />
        <StatsWrapper>
          <table>
            <tbody>
              <tr>
                <StatsLabel>Nb factures</StatsLabel>
                <StatsValue>{nb}</StatsValue>
              </tr>
              <tr>
                <StatsLabel>Nb factures TESTEES</StatsLabel>
                <StatsValue>{nbTested}</StatsValue>
                <StatsValue>{((nbTested * 100) / nb).toFixed(2)}%</StatsValue>
              </tr>
              <tr>
                <StatsLabel>Nb factures OK</StatsLabel>
                <StatsValue>{nbOk}</StatsValue>
                <StatsValue>{((nbOk * 100) / nbTested).toFixed(2)}%</StatsValue>(des factures testées)
              </tr>
            </tbody>
          </table>
        </StatsWrapper>

        <DownloadButton data={eInvoiceList} fileName="eInvoices" label={null} size="medium" />
      </Header>
      <StatusListSelect statusList={statusList} filters={filters || []} setFilters={setFilters} />

      <TableInfinite<TInvoiceEinvoiceLinkEnriched>
        data={displayListFiltered}
        definitionFields={definitionFields({ onSelect })}
        nextPage={null}
        isQueriedAllCompleted={true}
        isLoading={isLoading}
      />

      {itemSelected && (
        <Dialog open={!!itemSelected} onClose={() => setItemSelected(null)} maxWidth="xl">
          <EinvoivingError
            open={!!itemSelected}
            results={JSON.parse(itemSelected.error)}
            onClose={() => setItemSelected(null)}
            invoiceId={Number(itemSelected.invoiceId)}
          />
        </Dialog>
      )}
    </Wrapper>
  )
}


const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 100px;
  margin-bottom: 20px;
`

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`
const StatsLabel = styled.td`
  padding-right: 15px;
`
const StatsValue = styled.td`
  font-weight: bold;
  text-align: center;
  background-color: lightgray;
  border-radius: 5px;
  padding: 2px;
  padding-left: 10px;
  padding-right: 10px;
`
